import React, { useState } from 'react';
import { Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources, scopes } from "../authConfig";
import { FaSave, FaTimes } from 'react-icons/fa';

function EntryUserEditForm({ user, onDone, onSuccess, onError }) {
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        id: user.userInfoId || "", // userInfoId → id
        surname: user.surname || "",
        givenName: user.givenName || "",
        displayName: user.displayName || "",
        // Keep the loginName for reference but it won't be editable
        loginName: user.loginName || ""
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            
            // Update displayName if surname or givenName changes
            if (name === 'surname' || name === 'givenName') {
                const surname = name === 'surname' ? value : prev.surname;
                const givenName = name === 'givenName' ? value : prev.givenName;
                updated.displayName = `${surname} ${givenName}`.trim();
            }
            
            return updated;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        
        try {
            // Validate required fields
            if (!formData.surname.trim() || !formData.givenName.trim()) {
                setError("姓と名は必須項目です。");
                setSaving(false);
                return;
            }

            // Prepare payload for API (UserEditFormに合わせたパラメーター名に変更)
            const user = {
                id: formData.id,
                displayName: formData.displayName,
                surname: formData.surname,
                givenName: formData.givenName,
                loginName: formData.loginName,
                accountEnabled: true // Always true as per requirements
            };

            console.log("call update");
            // Call API to update user (UserEditFormと同じ方式に変更)
            const response = await execute(
                'PUT',
                `${protectedResources.updateUser.endpoint}`,
                user
            );
            
            if (response && response.success) {
                // 成功時の処理
                if (onSuccess) onSuccess("ユーザー情報が更新されました。");
                onDone(true); // Pass true to indicate update was successful
            } else {
                setError(response?.message || "ユーザー情報の更新に失敗しました。");
                if (onError) onError("ユーザー情報の更新に失敗しました。");
            }
        } catch (error) {
            console.error("Error updating user:", error);
            setError("ユーザー情報の更新中にエラーが発生しました。");
            if (onError) onError("ユーザー情報の更新中にエラーが発生しました。");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            
            <Row className="g-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label className="fw-medium">姓 <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                            type="text" 
                            name="surname" 
                            value={formData.surname} 
                            onChange={handleChange} 
                            className="border-secondary-subtle"
                            placeholder="姓"
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label className="fw-medium">名 <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                            type="text" 
                            name="givenName" 
                            value={formData.givenName} 
                            onChange={handleChange} 
                            className="border-secondary-subtle"
                            placeholder="名"
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label className="fw-medium">メールアドレス</Form.Label>
                        <Form.Control 
                            type="email" 
                            value={formData.loginName} 
                            className="bg-light"
                            disabled
                        />
                        <Form.Text className="text-muted">
                            メールアドレスは編集できません
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>
            
            <div className="d-flex justify-content-end gap-2 mt-4">
                <Button 
                    variant="outline-secondary" 
                    onClick={() => onDone(false)}
                    className="d-flex align-items-center gap-1"
                >
                    <FaTimes /> キャンセル
                </Button>
                <Button 
                    variant="primary" 
                    type="submit"
                    className="d-flex align-items-center gap-1 px-4"
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            保存中...
                        </>
                    ) : (
                        <>
                            <FaSave /> 保存
                        </>
                    )}
                </Button>
            </div>
        </Form>
    );
}

export default EntryUserEditForm;